import React from "react";
import { Box, Sheet, Table, Typography } from "@mui/joy";
import { DashboardColumn, DashboardVisualKind } from "../types";

type JsonObject = Record<string, unknown>;

const objectData = (data: unknown): JsonObject =>
  data && typeof data === "object" && !Array.isArray(data)
    ? (data as JsonObject)
    : {};

const numberValue = (value: unknown): number =>
  typeof value === "number" && Number.isFinite(value) ? value : 0;

const formatValue = (value: unknown, unit?: string | null): string => {
  if (typeof value === "number") {
    const formatted = new Intl.NumberFormat(undefined, {
      maximumFractionDigits: 2,
    }).format(value);
    return unit ? `${formatted} ${unit}` : formatted;
  }
  if (typeof value === "string") return unit ? `${value} ${unit}` : value;
  return "—";
};

const Sparkline: React.FC<{ data: JsonObject }> = ({ data }) => {
  const series = Array.isArray(data.series) ? data.series : [];
  const points = series.flatMap((entry) => {
    if (!entry || typeof entry !== "object") return [];
    const candidate = (entry as JsonObject).points;
    return Array.isArray(candidate) ? candidate : [];
  });
  const values = points.map((point) =>
    point && typeof point === "object"
      ? numberValue((point as JsonObject).value)
      : 0,
  );
  const maximum = Math.max(1, ...values);
  const minimum = Math.min(0, ...values);
  const spread = Math.max(1, maximum - minimum);
  const coordinates = values.map((value, index) => {
    const x = values.length <= 1 ? 4 : 4 + (172 * index) / (values.length - 1);
    const y = 42 - ((value - minimum) / spread) * 34;
    return `${x},${y}`;
  });

  return (
    <Box sx={{ minWidth: 0 }}>
      <Box
        component="svg"
        viewBox="0 0 180 48"
        role="img"
        aria-label={`${series.length || 1} time series`}
        sx={{
          display: "block",
          width: "100%",
          height: 112,
          overflow: "visible",
        }}
      >
        <line
          x1="4"
          y1="43"
          x2="176"
          y2="43"
          stroke="var(--joy-palette-neutral-200)"
        />
        {coordinates.length > 0 && (
          <polyline
            points={coordinates.join(" ")}
            fill="none"
            stroke="var(--joy-palette-primary-500)"
            strokeWidth="2.5"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        )}
      </Box>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        {series.map((entry, index) => {
          const name =
            entry &&
            typeof entry === "object" &&
            typeof (entry as JsonObject).name === "string"
              ? String((entry as JsonObject).name)
              : `Series ${index + 1}`;
          return (
            <Typography
              key={`${name}-${index}`}
              level="body-xs"
              color="neutral"
            >
              {name}
            </Typography>
          );
        })}
      </Box>
    </Box>
  );
};

const Breakdown: React.FC<{ data: JsonObject }> = ({ data }) => {
  const items = Array.isArray(data.items) ? data.items : [];
  const values = items.map((item) =>
    item && typeof item === "object"
      ? numberValue((item as JsonObject).value ?? (item as JsonObject).count)
      : 0,
  );
  const maximum = Math.max(1, ...values);
  return (
    <Box sx={{ display: "grid", gap: 1.25 }}>
      {items.map((item, index) => {
        const row =
          item && typeof item === "object" ? (item as JsonObject) : {};
        const value = values[index];
        return (
          <Box key={`${String(row.label ?? row.status ?? index)}-${index}`}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 1,
                mb: 0.5,
              }}
            >
              <Typography
                level="body-sm"
                sx={{
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {String(row.label ?? row.status ?? `Item ${index + 1}`)}
              </Typography>
              <Typography level="body-sm" fontWeight="lg">
                {formatValue(
                  value,
                  typeof data.unit === "string" ? data.unit : undefined,
                )}
              </Typography>
            </Box>
            <Box
              sx={{
                height: 8,
                borderRadius: 8,
                bgcolor: "neutral.100",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  width: `${(value / maximum) * 100}%`,
                  height: "100%",
                  borderRadius: 8,
                  bgcolor: "primary.500",
                }}
              />
            </Box>
          </Box>
        );
      })}
      {items.length === 0 && (
        <Typography level="body-sm" color="neutral">
          No categories in this range.
        </Typography>
      )}
    </Box>
  );
};

const DashboardDataTable: React.FC<{ data: JsonObject }> = ({ data }) => {
  const columns = Array.isArray(data.columns)
    ? (data.columns as DashboardColumn[])
    : [];
  const rows = Array.isArray(data.rows)
    ? (data.rows as Record<string, unknown>[])
    : [];
  return (
    <Sheet variant="outlined" sx={{ overflowX: "auto", borderRadius: "sm" }}>
      <Table size="sm" borderAxis="xBetween" stickyHeader>
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.key}>{column.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={String(row.id ?? `${index}`)}>
              {columns.map((column) => (
                <td key={column.key}>{formatValue(row[column.key])}</td>
              ))}
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={Math.max(1, columns.length)}>
                No rows in this range.
              </td>
            </tr>
          )}
        </tbody>
      </Table>
      {typeof data.total_rows === "number" && (
        <Typography level="body-xs" color="neutral" sx={{ px: 1.5, py: 1 }}>
          {data.total_rows} rows
        </Typography>
      )}
    </Sheet>
  );
};

const StatusDistribution: React.FC<{ data: JsonObject }> = ({ data }) => {
  const items = Array.isArray(data.items) ? data.items : [];
  const total = items.reduce(
    (sum, item) =>
      sum +
      (item && typeof item === "object"
        ? numberValue((item as JsonObject).count)
        : 0),
    0,
  );
  const colors = [
    "success.500",
    "warning.500",
    "danger.500",
    "primary.500",
    "neutral.500",
  ];
  return (
    <Box>
      <Box
        aria-label="Status distribution"
        sx={{
          display: "flex",
          overflow: "hidden",
          height: 12,
          borderRadius: 12,
          bgcolor: "neutral.100",
        }}
      >
        {items.map((item, index) => {
          const count =
            item && typeof item === "object"
              ? numberValue((item as JsonObject).count)
              : 0;
          const status =
            item && typeof item === "object"
              ? String((item as JsonObject).status ?? `Status ${index + 1}`)
              : `Status ${index + 1}`;
          return (
            <Box
              key={`${status}-${index}`}
              title={`${status}: ${count}`}
              sx={{
                width: `${total > 0 ? (count / total) * 100 : 0}%`,
                bgcolor: colors[index % colors.length],
              }}
            />
          );
        })}
      </Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mt: 1.5 }}>
        {items.map((item, index) => {
          const row =
            item && typeof item === "object" ? (item as JsonObject) : {};
          return (
            <Typography
              key={`${String(row.status ?? index)}-${index}`}
              level="body-xs"
              color="neutral"
            >
              <Box
                component="span"
                sx={{
                  display: "inline-block",
                  width: 8,
                  height: 8,
                  borderRadius: 8,
                  bgcolor: colors[index % colors.length],
                  mr: 0.75,
                }}
              />
              {String(row.status ?? `Status ${index + 1}`)}{" "}
              {numberValue(row.count)}
            </Typography>
          );
        })}
      </Box>
    </Box>
  );
};

export const DashboardVisual: React.FC<{
  kind: DashboardVisualKind;
  data: unknown;
}> = ({ kind, data }) => {
  const value = objectData(data);
  if (kind === "kpi") {
    const unit = typeof value.unit === "string" ? value.unit : undefined;
    const comparison = value.comparison;
    return (
      <Box>
        <Typography
          level="h2"
          sx={{ fontSize: { xs: "1.7rem", sm: "2rem" }, lineHeight: 1.15 }}
        >
          {formatValue(value.value, unit)}
        </Typography>
        {comparison !== undefined && comparison !== null && (
          <Typography level="body-sm" color="success" sx={{ mt: 0.75 }}>
            {typeof comparison === "number" && comparison > 0 ? "+" : ""}
            {formatValue(comparison, unit)}{" "}
            {String(value.comparison_label ?? "vs previous period")}
          </Typography>
        )}
      </Box>
    );
  }
  if (kind === "time_series") return <Sparkline data={value} />;
  if (kind === "breakdown" || kind === "bar") return <Breakdown data={value} />;
  if (kind === "table") return <DashboardDataTable data={value} />;
  if (kind === "status_distribution")
    return <StatusDistribution data={value} />;
  return (
    <Typography level="body-sm" color="neutral">
      This visual type is not supported.
    </Typography>
  );
};
